import { inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';

import { WINDOW } from '@hglobal/fin-ui';

import { BusinessProfile } from '@zeusm/shared/core/entities';
import { ProfileType } from '@zeusm/shared/core/enums';

import { ACTIVE_BUSINESS_PROFILE_ID_KEY, ACTIVE_PROFILE_KEY } from '../helpers';
import { ProfileSwitchContext } from '../models';
import { ProfileRouteService } from './profile-route.service';

const DEFAULT_PROFILE: Extract<ProfileType, ProfileType.USER> = ProfileType.USER;

type SetActiveProfileOptions = {
  navigateToRoot?: boolean;
};

type SetActiveBusinessProfileOptions = {
  navigateToRoot: boolean;
  reloadOnSwitch: boolean;
};

@Injectable({ providedIn: 'root' })
export class ProfileSwitchService {
  readonly #router = inject(Router);
  readonly #window = inject<Window>(WINDOW);
  readonly #profileRouteService = inject(ProfileRouteService);

  readonly #activeProfile = signal<ProfileType | null>(DEFAULT_PROFILE);
  readonly #activeBusinessProfileId = signal<string | null>(null);

  readonly activeProfile = this.#activeProfile.asReadonly();
  readonly activeBusinessProfileId = this.#activeBusinessProfileId.asReadonly();
  readonly profileChange = signal<ProfileType | null>(null);

  initializeActiveProfile(context: ProfileSwitchContext): void {
    this.#initializeActiveBusinessProfileId(context);

    const saved = localStorage.getItem(ACTIVE_PROFILE_KEY);
    if (this.canSetProfile(saved, context)) {
      this.#activeProfile.set(saved);
      if (saved === ProfileType.BUSINESS) {
        this.#ensureActiveBusinessProfileId(context);
      }
      return;
    }

    const fallbackProfile = this.#resolveFallbackProfile(context);
    this.#activeProfile.set(fallbackProfile);
    localStorage.setItem(ACTIVE_PROFILE_KEY, fallbackProfile);

    if (fallbackProfile === ProfileType.BUSINESS) {
      this.#ensureActiveBusinessProfileId(context);
    }
  }

  setActiveProfile(profile: ProfileType, context: ProfileSwitchContext, options: SetActiveProfileOptions = {}): void {
    const { navigateToRoot = true } = options;
    const previousProfile = this.#activeProfile();
    const didChangeProfileSection =
      this.#profileRouteService.getProfileRoot(previousProfile) !== this.#profileRouteService.getProfileRoot(profile);

    if (profile === ProfileType.BUSINESS && !this.#ensureActiveBusinessProfileId(context)) return;
    if (!this.canSetProfile(profile, context)) return;
    if (previousProfile === ProfileType.BUSINESS && profile !== ProfileType.BUSINESS) {
      this.#clearActiveBusinessProfileId();
    }

    this.#activeProfile.set(profile);
    localStorage.setItem(ACTIVE_PROFILE_KEY, profile);
    this.profileChange.set(profile);

    if (didChangeProfileSection && navigateToRoot) {
      this.#navigateToProfileRoot(profile);
    }
  }

  setActiveBusinessProfile(profileId: string, context: ProfileSwitchContext): void {
    this.#activateBusinessProfile(profileId, context, { navigateToRoot: true, reloadOnSwitch: true });
  }

  ensurePersonalProfileActive(context: ProfileSwitchContext): boolean {
    if (!this.canSetProfile(ProfileType.USER, context)) {
      return false;
    }

    this.setActiveProfile(ProfileType.USER, context, { navigateToRoot: false });

    return this.activeProfile() === ProfileType.USER;
  }

  ensureBusinessProfileActive(context: ProfileSwitchContext, profileId?: string | null): boolean {
    const targetProfileId = this.#resolveBusinessProfileId(context, profileId);
    if (!targetProfileId) {
      return false;
    }

    this.#activateBusinessProfile(targetProfileId, context, { navigateToRoot: false, reloadOnSwitch: false });

    return this.activeProfile() === ProfileType.BUSINESS && !!this.activeBusinessProfileId();
  }

  ensureSupportProfileActive(context: ProfileSwitchContext): boolean {
    if (!this.canSetProfile(ProfileType.SUPPORT, context)) {
      return false;
    }

    this.setActiveProfile(ProfileType.SUPPORT, context, { navigateToRoot: false });

    return this.activeProfile() === ProfileType.SUPPORT;
  }

  canSetProfile(profile: string | null, context: ProfileSwitchContext): profile is ProfileType {
    if (profile === ProfileType.TREASURER) return context.isSuperUser && context.hasPersonalProfileAccess;
    if (profile === ProfileType.USER) return context.hasPersonalProfileAccess;
    if (profile === ProfileType.BUSINESS) return context.businessProfiles.length > 0;
    if (profile === ProfileType.SUPPORT) return context.hasSupportProfileAccess;

    return false;
  }

  resolveSelectedBusinessProfile(context: ProfileSwitchContext): BusinessProfile | null {
    const businessProfileId = this.#activeBusinessProfileId();
    if (!businessProfileId) {
      return context.businessProfiles.at(0) ?? null;
    }

    return context.businessProfiles.find(profile => profile.id === businessProfileId) ?? null;
  }

  clearActiveState(): void {
    this.#activeProfile.set(null);
    this.#activeBusinessProfileId.set(null);
    this.profileChange.set(null);
  }

  #activateBusinessProfile(profileId: string, context: ProfileSwitchContext, options: SetActiveBusinessProfileOptions): void {
    if (!this.#canSetBusinessProfile(profileId, context)) return;

    const { navigateToRoot, reloadOnSwitch } = options;
    const wasBusinessProfileActive = this.#activeProfile() === ProfileType.BUSINESS;
    const currentBusinessProfileId = this.#activeBusinessProfileId();
    const isSwitchBetweenBusinessProfiles =
      wasBusinessProfileActive && !!currentBusinessProfileId && currentBusinessProfileId !== profileId;

    this.#setActiveBusinessProfileId(profileId);
    this.setActiveProfile(ProfileType.BUSINESS, context, { navigateToRoot });

    if (reloadOnSwitch && isSwitchBetweenBusinessProfiles) {
      this.#window.location.reload();
    }
  }

  #resolveFallbackProfile(context: ProfileSwitchContext): ProfileType {
    if (context.hasSupportProfileAccess) {
      return ProfileType.SUPPORT;
    }

    if (this.canSetProfile(DEFAULT_PROFILE, context)) {
      return DEFAULT_PROFILE;
    }

    if (this.canSetProfile(ProfileType.BUSINESS, context)) {
      return ProfileType.BUSINESS;
    }

    return DEFAULT_PROFILE;
  }

  #initializeActiveBusinessProfileId(context: ProfileSwitchContext): void {
    const saved = localStorage.getItem(ACTIVE_BUSINESS_PROFILE_ID_KEY);
    if (this.#canSetBusinessProfile(saved, context)) {
      this.#activeBusinessProfileId.set(saved);
      return;
    }

    if (!context.hasPersonalProfileAccess) {
      const firstBusinessProfile = context.businessProfiles.at(0);
      if (!firstBusinessProfile) {
        this.#clearActiveBusinessProfileId();
        return;
      }

      if (this.#activeBusinessProfileId() !== firstBusinessProfile.id) {
        this.#setActiveBusinessProfileId(firstBusinessProfile.id);
      }
      return;
    }

    this.#activeBusinessProfileId.set(null);
  }

  #canSetBusinessProfile(profileId: string | null | undefined, context: ProfileSwitchContext): profileId is string {
    const normalizedId = profileId?.trim();
    if (!normalizedId) return false;

    return context.businessProfiles.some(businessProfile => businessProfile.id === normalizedId);
  }

  #setActiveBusinessProfileId(profileId: string): void {
    this.#activeBusinessProfileId.set(profileId);
    localStorage.setItem(ACTIVE_BUSINESS_PROFILE_ID_KEY, profileId);
  }

  #clearActiveBusinessProfileId(): void {
    this.#activeBusinessProfileId.set(null);
    localStorage.removeItem(ACTIVE_BUSINESS_PROFILE_ID_KEY);
  }

  #ensureActiveBusinessProfileId(context: ProfileSwitchContext): boolean {
    const businessProfileId = this.#resolveBusinessProfileId(context);
    if (!businessProfileId) return false;

    if (this.#activeBusinessProfileId() !== businessProfileId) {
      this.#setActiveBusinessProfileId(businessProfileId);
    }

    return true;
  }

  #resolveBusinessProfileId(context: ProfileSwitchContext, profileId?: string | null): string | null {
    const normalizedProfileId = profileId?.trim();
    if (normalizedProfileId && this.#canSetBusinessProfile(normalizedProfileId, context)) {
      return normalizedProfileId;
    }

    const activeBusinessProfileId = this.#activeBusinessProfileId();
    if (activeBusinessProfileId && this.#canSetBusinessProfile(activeBusinessProfileId, context)) {
      return activeBusinessProfileId;
    }

    return context.businessProfiles.at(0)?.id ?? null;
  }

  #navigateToProfileRoot(profile: ProfileType): void {
    const targetRoute = this.#profileRouteService.getProfileRoot(profile)!;
    if (this.#router.url === targetRoute || this.#router.url.startsWith(`${targetRoute}/`)) {
      return;
    }

    void this.#router.navigateByUrl(targetRoute);
  }
}
